/**
 * Cliente API para /api/admin/users/:userId/memory/* (fase 26.D).
 */
import { deleteJsonAdmin, get, postJsonAdmin } from './_core.js';

export interface MemoryFactItem {
  id: string;
  scope: 'profile' | 'module_shared' | 'module_private';
  originModule: string;
  kind: string;
  /**
   * `null` cuando el fact pertenece a un módulo sensible (gate de
   * privacidad de Coach): el backend no expone el body — ver `gated`.
   */
  body: string | null;
  /**
   * English text the fact is retrieved by (embedding + keyword search); the
   * body is the learner's L1 display text. `null` on facts without one
   * (legacy, encrypted, owner-edited) and on gated facts.
   */
  indexText: string | null;
  /** true = fact de módulo sensible: body excluido server-side. */
  gated?: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  disabledAt: string | null;
  disabledBy: string | null;
  disabledReason: string | null;
}

export interface EpisodicItem {
  id: string;
  moduleSlug: string;
  sessionId: string;
  kind: 'opener' | 'topic' | 'user_signal' | 'agent_observation';
  body: string;
  metadata: Record<string, unknown>;
  occurredAt: string;
  expiresAt: string;
  recallCount: number;
  lastRecalledAt: string | null;
  disabledAt: string | null;
  disabledReason: string | null;
}

export interface ConsolidationItem {
  id: string;
  trigger: 'cron' | 'admin' | 'bootstrap';
  triggeredBy: string | null;
  expiredCount: number;
  promotedCount: number;
  promotionCandidates: number;
  promotedFactIds: string[];
  deletedEpisodicIds: string[];
  durationMs: number;
  errorMessage: string | null;
  spanId: string | null;
  startedAt: string;
  endedAt: string;
}

export interface MemoryFactsFilters {
  scope?: MemoryFactItem['scope'];
  kind?: string;
  origin_module?: string;
  /** Filtra los facts producidos por el distill de una sesión (metadata.sessionId). */
  session_id?: string;
  include_disabled?: boolean;
  limit?: number;
  cursor?: string;
}

export interface EpisodicFilters {
  module?: string;
  kind?: EpisodicItem['kind'];
  include_disabled?: boolean;
  limit?: number;
}

function buildQs(input: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === '' || value === false) continue;
    params.set(key, typeof value === 'boolean' ? String(value) : String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchUserMemoryFacts(
  userId: string,
  filters: MemoryFactsFilters = {},
): Promise<{ items: MemoryFactItem[]; nextCursor: string | undefined }> {
  const qs = buildQs({
    ...(filters.scope ? { scope: filters.scope } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.origin_module ? { origin_module: filters.origin_module } : {}),
    ...(filters.session_id ? { session_id: filters.session_id } : {}),
    ...(filters.include_disabled ? { include_disabled: 'true' } : {}),
    ...(filters.limit ? { limit: filters.limit } : {}),
    ...(filters.cursor ? { cursor: filters.cursor } : {}),
  });
  const res = await get<{ items: MemoryFactItem[]; nextCursor?: string }>(
    `/admin/users/${encodeURIComponent(userId)}/memory/facts${qs}`,
  );
  return { items: res.data.items, nextCursor: res.data.nextCursor };
}

export async function fetchUserMemoryEpisodic(
  userId: string,
  filters: EpisodicFilters = {},
): Promise<{ items: EpisodicItem[] }> {
  const qs = buildQs({
    ...(filters.module ? { module: filters.module } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.include_disabled ? { include_disabled: 'true' } : {}),
    ...(filters.limit ? { limit: filters.limit } : {}),
  });
  const res = await get<{ items: EpisodicItem[] }>(
    `/admin/users/${encodeURIComponent(userId)}/memory/episodic${qs}`,
  );
  return { items: res.data.items };
}

export async function fetchUserMemoryConsolidations(
  userId: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<{ items: ConsolidationItem[]; nextCursor: string | undefined }> {
  const qs = buildQs({
    ...(opts.limit ? { limit: opts.limit } : {}),
    ...(opts.cursor ? { cursor: opts.cursor } : {}),
  });
  const res = await get<{ items: ConsolidationItem[]; nextCursor?: string }>(
    `/admin/users/${encodeURIComponent(userId)}/memory/consolidations${qs}`,
  );
  return { items: res.data.items, nextCursor: res.data.nextCursor };
}

export async function disableMemoryFact(
  userId: string,
  factId: string,
  reason: string,
): Promise<void> {
  await postJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/memory/facts/${factId}/disable`,
    { reason },
  );
}

export async function enableMemoryFact(userId: string, factId: string): Promise<void> {
  await postJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/memory/facts/${factId}/enable`,
    {},
  );
}

export async function deleteMemoryFact(
  userId: string,
  factId: string,
  reason: string,
): Promise<void> {
  await deleteJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/memory/facts/${factId}`,
    { reason },
  );
}

export async function disableEpisodicEvent(
  userId: string,
  eventId: string,
  reason: string,
): Promise<void> {
  await postJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/memory/episodic/${eventId}/disable`,
    { reason },
  );
}

export async function deleteEpisodicEvent(
  userId: string,
  eventId: string,
  reason: string,
): Promise<void> {
  await deleteJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/memory/episodic/${eventId}`,
    { reason },
  );
}

export async function enqueueMemoryConsolidate(
  userId: string,
): Promise<{ jobId: string }> {
  return postJsonAdmin<{ jobId: string }>(
    `/admin/users/${encodeURIComponent(userId)}/memory/consolidate`,
    {},
  );
}
